/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
import { logger, sanitizeError } from "../../utils/logger.js";
/**
 * @fileoverview Register logic module.
 * Handles user registration operations including fetching
 * sector data for the registration form.
 * @module logic/register
 */

import {
  CONTENT_TYPE_JSON,
  TOKEN_EXPIRED_MESSAGE,
} from "../../utils/apiConstants.js";
import { refreshToken, signOut } from "../../utils/common";
import { apiMessageOrGeneric } from "../../utils/genericErrorMessages.js";

/**
 * Fetches all sectors for registration form dropdown.
 * @async
 * @param {Function} navigate - React Router navigation function
 * @returns {Promise<Array|null>} Array of sector objects or null on error
 */
export const fetchSectors = async (navigate) => {
  const options = {
    method: "GET",
    headers: {
      "Content-Type": CONTENT_TYPE_JSON,
    },
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}sector`,
      options,
    );
    const data = await response.json();

    if (data?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        navigate("/");
        return null;
      }
      return await fetchSectors(navigate);
    }

    if (!response.ok) {
      throw new Error(
        apiMessageOrGeneric(data?.message, "FAILED_TO_FETCH_SECTORS"),
      );
    }

    return data;
  } catch (error) {
    logger.error("Error fetching sectors:", { err: sanitizeError(error) });
    return null;
  }
};
